/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import isNil from 'lodash.isnil';
import {AppLink} from '@ion/applications/src/link/AppLink';
import {translate} from '@ion/core/src/locale/locale';
import {getQueryString} from '@ion/core/src/utils/QueryStringUtils';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {Notification} from '@ion/dls/src/types/extensions';
import {DLSTheme} from '@ion/dls/src/types/types';
import useEnvQueryParam from '../hooks/useEnvQueryParam';

export interface ViewEventCtaProps {
  notification?: Notification;
}

const useStyles = makeStyles((theme: DLSTheme) => {
  return {
    viewEventCta : {
      marginLeft : '25px',
      padding    : '4px 4px',
      fontSize   : '14px',
      '& a'      : {
        color          : dlsColor('aaBlue'),
        textDecoration : 'none',
        '&:hover'      : {
          textDecoration : 'underline'
        },
        '&:active, &:focus' : {
          color               : dlsColor('grey1'),
          textDecoration      : 'underline',
          textDecorationColor : dlsColor('grey1')
        }
      }
    }
  };
});

/**
 * "View Event" deep link CTA component suitable for passing to the DLS
 * NotificationCard component via its viewEventCta prop. This component
 * CTA exists separately from the NotificationCard component because the
 * NotificationCard component is in the DLS package, and this component
 * incorporates the AppLink component which is in the applications package.
 * Since the applications package depends on the DLS package, if this
 * component were part of the NotificationCard in the DLS, it would create
 * a circular dependency.
 *
 * ```typescript
 *
 *  import ViewEventCta from './ViewEventCta';
 *
 *  <NotificationCard
 *    notification={notification}
 *    viewEventCta={<ViewEventCta notification={notification} />}
 *    ... />
 *
 * ```
 *
 */
export const ViewEventCta = ({
  notification
}: ViewEventCtaProps) => {
  const classes = useStyles();
  const env     = useEnvQueryParam();
  const {assetId, createdOn, avlMessageUuid} = notification;
  const isValid = !isNil(assetId) && !isNil(createdOn) && !isNil(avlMessageUuid);
  if (isValid) {
    const href = `/tracking?${getQueryString({
      ...env,
      report  : 'assetHistory',
      eventId : avlMessageUuid,
      date    : createdOn,
      assetId
    })}`;
    return (
      <TertiaryButton className={classes.viewEventCta}>
        <AppLink href={href}>
          {translate('notifications:panel.viewEvent')}
        </AppLink>
      </TertiaryButton>
    );
  }
  return null;
};

export default ViewEventCta;
